import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyPageMeta } from '../lib/pageMeta';
import { metaForPath } from '../lib/prerenderMeta';

const ARTICLE_PATH = /^\/updates\/[^/]+$/;
const PROFILE_PATH = /^\/team\/[^/]+$/;

/** Syncs html lang, body class, and page meta tags when route or locale changes. */
export default function PageMeta() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const locale = i18n.language?.startsWith('vi') ? 'vi' : 'en';
    const isVi = locale === 'vi';
    document.body.classList.toggle('lang-vi', isVi);

    // A direct article request already has correct server-rendered SEO metadata.
    // Preserve it until ArticlePage has loaded CMS data. For client-side navigation
    // there is no server marker, so use generic Updates metadata temporarily.
    if (ARTICLE_PATH.test(pathname)) {
      const hasServerArticleMeta = Boolean(
        document.getElementById('jsonld-article-server') ||
          document.getElementById('seo-article-snapshot'),
      );
      if (hasServerArticleMeta) return;

      const interim = metaForPath('/updates', 'en');
      applyPageMeta({
        ...interim,
        lang: locale,
        ogLocale: locale === 'vi' ? 'vi_VN' : 'en_US',
        pathname,
      });
      return;
    }

    // Profiles remain prerendered and can use their build-time payload immediately.
    if (PROFILE_PATH.test(pathname)) {
      const interim = metaForPath(pathname, 'en');
      applyPageMeta({
        ...interim,
        lang: locale,
        ogLocale: locale === 'vi' ? 'vi_VN' : 'en_US',
        pathname,
      });
      return;
    }

    const meta = metaForPath(pathname, locale);
    applyPageMeta({ ...meta, pathname });
  }, [pathname, i18n.language]);

  return null;
}
